// Generated by tutorials/record.cjs from tutorials/articles/h17-using-analysis.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h17 --text-only` (wording) or
// `node tutorials/record.cjs h17` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'using-analysis',
  catalogId: 'h17',
  title: 'Using Analysis',
  summary: 'Analysis puts the older analysis screens on one page: diet orders, allergies, special needs, eating devices and served meals and items. Read the answer at a glance, compare centers or units, see who is behind any number, and save or export what you find.',
  audience: 'Dietary managers, dietitians and corporate staff who review numbers across residents, units or centers',
  area: 'reports',
  learn: [
    'Open Analysis and pick a topic',
    'Read the Summary and see who is behind a number',
    'Compare centers or units, and read the shading',
    'Use Needs a look',
    'See diet changes, durations and tube feeding, and filter by diet order',
    'Find meals with no record',
    'Build your own table, or switch to the classic layout',
    'Save a view, and export to Excel or PDF',
  ],
  keywords: [
    'analysis',
    'diet order analysis',
    'food allergy analysis',
    'special needs analysis',
    'adaptive eating devices',
    'special devices',
    'served meals',
    'served items',
    'employee served items',
    'pivot',
    'compare centers',
    'corporate',
    'diet changes',
    'tube feed',
    'diet rx',
    'missing meals',
    'saved view',
    'export',
    'excel',
    'pdf',
  ],
  steps: [
    {
      id: 'open-analysis',
      part: 'Open Analysis',
      heading: 'Open Analysis',
      text: 'Select Reports in the menu, then Analysis. The older analysis screens are all on this one page now. It opens on the topic you used last, or on Diet Orders the first time.',
      media: {
        image: {
          src: '/help/using-analysis/open-analysis.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/open-analysis.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Analysis page on Diet Orders: the topic tabs along the top, the Summary view, Needs a look, and a ranked list of diets.',
      },
    },
    {
      id: 'pick-topic',
      part: 'Open Analysis',
      heading: 'Pick a topic',
      text: 'The topics are tabs: Diet Orders, Food Allergies, Special Needs, Adaptive Eating Devices, Served Meals, Served Items and Employee Served Items. Select one to open it. Each has the same views: Summary, Compare, Trend and Explore.\n\nSummary is the answer, ranked, with the largest first. Rows with nobody in them are left out. On Special Needs, safety needs such as Aspiration Precaution are always listed first.',
      media: {
        image: {
          src: '/help/using-analysis/pick-topic.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/pick-topic.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'Special Needs open on Summary: Residents with each special need, with a Safety group at the top listing Aspiration Precaution first.',
      },
    },
    {
      id: 'who-is-behind',
      part: 'Open Analysis',
      heading: 'See who is behind a number',
      text: 'Select any bar or number to see the residents behind it, in a panel at the side. Close the panel with the X, or press Esc.',
      media: {
        image: {
          src: '/help/using-analysis/who-is-behind.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/who-is-behind.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.7,
        },
        alt: 'A side panel titled Aspiration Precaution listing the residents with that need, with their center and unit.',
      },
    },
    {
      id: 'compare',
      part: 'Compare centers or units',
      heading: 'Compare side by side',
      text: 'Select Compare to put the centers side by side. Each cell is the share of that center\'s residents. Warmer colors are above the average for all centers and cooler ones are below. ▲ and ▼ mark the big differences.\n\nAt a single center, Compare puts its units side by side.',
      media: {
        image: {
          src: '/help/using-analysis/compare.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/compare.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'Diet Orders on Compare: one row per center, a column per diet, cells shaded warm or cool, and arrows on the cells far from the average.',
      },
    },
    {
      id: 'count',
      part: 'Compare centers or units',
      heading: 'Percent or count',
      text: 'Under Show, % of residents compares centers of different sizes fairly. Select Count to see the number of residents instead. Under Compare by, Units compares units rather than centers.',
      media: {
        image: {
          src: '/help/using-analysis/count.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/count.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.6,
        },
        alt: 'The same comparison showing Count: the number of residents on each diet at each center.',
      },
    },
    {
      id: 'needs-a-look',
      part: 'Compare centers or units',
      heading: 'Check what Needs a look',
      text: 'Needs a look, above the numbers, flags up to three centers or units far from the average. Select one to see the residents behind it. On Served Meals, it flags meals with no record.',
      media: {
        image: {
          src: '/help/using-analysis/needs-a-look.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/needs-a-look.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.8,
        },
        alt: 'Needs a look with three flags, such as Cedar Point: Renal 19% of residents against 9.1% across all centers, and the residents behind it in the side panel.',
      },
    },
    {
      id: 'changes',
      part: 'Diet order changes and durations',
      heading: 'See diet changes',
      text: 'On Diet Orders, Summary has four modes. Current mix is what residents are on today. Changes shows what moved from one order to another during the Period you pick at the top, such as the last 14 days.',
      media: {
        image: {
          src: '/help/using-analysis/changes.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/changes.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.8,
        },
        alt: 'Diet Orders on Changes: Diet changes in the last 14 days, and a ranked list of changes such as Regular to NPO.',
      },
    },
    {
      id: 'duration',
      part: 'Diet order changes and durations',
      heading: 'Duration and Tube Feed',
      text: 'Duration shows how long residents have been on their current order, with the average and longest for each diet. Tube Feed lists the residents on tube feeding, longest first.',
      media: {
        image: {
          src: '/help/using-analysis/duration.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/duration.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.6,
        },
        alt: 'Diet Orders on Duration: Days on current order in five ranges, and a table of residents, average days and longest for each diet.',
      },
    },
    {
      id: 'diet-rx',
      part: 'Diet order changes and durations',
      heading: 'Look at certain diet orders',
      text: 'Select Diet Rx to look at certain diet orders only. Check up to five diet types and five consistencies, then choose how they match:\n\nContains counts orders that have them, maybe with more. Matches counts orders that are exactly those, and nothing else.\n\nSelect Done. The filter applies to every view until you clear it.',
      media: {
        image: {
          src: '/help/using-analysis/diet-rx.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/diet-rx.mp4',
          width: 1280,
          height: 748,
          durationSec: 4,
        },
        alt: 'The Diet Rx filter open with Renal checked and Contains chosen under Orders that, beside the residents on a Renal diet.',
      },
    },
    {
      id: 'served-meals',
      part: 'Find meals with no record',
      heading: 'Find meals with no record',
      text: 'Served Meals shows every meal of the Period as a calendar, one square per meal. Darker squares mean more of the census was served.\n\nA striped red square is a meal with no record, which usually means tickets were not printed or the meal was not recorded.',
      media: {
        image: {
          src: '/help/using-analysis/served-meals.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/served-meals.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.6,
        },
        alt: 'Served Meals: totals for meals recorded, coverage and meals with no record, and a calendar of every meal with some squares striped red.',
      },
    },
    {
      id: 'missing-meal',
      part: 'Find meals with no record',
      heading: 'Open a missing meal',
      text: 'Select a square to see that meal center by center. The center that served 0 residents is the one with no record.',
      media: {
        image: {
          src: '/help/using-analysis/missing-meal.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/missing-meal.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.7,
        },
        alt: 'A side panel for one breakfast listing each center\'s residents served, with Hillcrest at 0.',
      },
    },
    {
      id: 'explore',
      part: 'Build your own table',
      heading: 'Explore',
      text: 'Explore builds any table. Instead of dragging fields, pick them from the lists: Rows, Then by, Columns and Show. Each group gets its own total. Select the arrow beside a group to close or open it, or use Expand all and Collapse all.',
      media: {
        image: {
          src: '/help/using-analysis/explore.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/explore.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Diet Orders on Explore: Rows Diet Type, Then by Unit, Columns Facility, with a total row for each diet and the Regular group closed.',
      },
    },
    {
      id: 'classic',
      part: 'Build your own table',
      heading: 'Switch to the classic layout',
      text: 'Prefer the table the older analysis screens opened with? Select Switch to Classic Layout. You can change the lists again from there.',
      media: {
        image: {
          src: '/help/using-analysis/classic.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/classic.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.4,
        },
        alt: 'Explore after Switch to Classic Layout: Rows Name, Then by Date, Then by Diet Order, one group per resident.',
      },
    },
    {
      id: 'save-view',
      part: 'Save and export',
      heading: 'Save a view',
      text: 'When a view is one you will want again, select Save view, type a name and select Save. It is added under Saved views, and one click opens it again with the same topic and settings.',
      media: {
        image: {
          src: '/help/using-analysis/save-view.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/using-analysis/save-view.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Saved views with the new view, Diet orders by resident, added after the ready-made ones.',
      },
    },
    {
      id: 'export',
      part: 'Save and export',
      heading: 'Export to Excel or PDF',
      text: 'Select the three dots at the end of the toolbar, then Export as Excel or Export as PDF. The file holds what you are looking at.',
      media: {
        image: {
          src: '/help/using-analysis/export.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The three-dot menu open at the end of the toolbar, offering Export as Excel and Export as PDF.',
      },
    },
  ],
  tips: [
    'Corporate login? Scope, at the top left, covers all your centers or just one. Pick one center to compare its units.',
    'Trend shows how a topic moved over time, such as each diet week by week. Food Allergies, Special Needs and Adaptive Eating Devices have no Trend yet, because no history is kept for them.',
    'Unit, Floor and Wing at the top narrow the numbers to part of the building. The Find box narrows a list to the rows you type, such as one diet.',
    'Ready-made views such as Monday center review, Safety needs and Missing meal records are under Saved views for everyone. One click opens each one.',
  ],
  notes: [
    'Your own saved views are kept in the browser you saved them in. On another computer, or after clearing your browser\'s data, save them again.',
    'A resident on two diets counts under each one, so the diet rows can add up to more than the number of residents.',
    'Links and bookmarks to the older analysis screens still work: each one opens its topic on this page.',
  ],
  related: ['running-a-report', 'getting-around'],
  recorded: '2026-10-26',
};

export default article;
